/**
 * useLocationTracking - Hook for officer mobile app GPS tracking
 * 
 * Automatically sends GPS pings to backend when officer has an active shift.
 * Features:
 * - Battery-aware intervals (30s moving, 2-5min stationary)
 * - Background tracking during active shift
 * - Auto-start on shift sign-on, auto-stop on sign-off
 * - Movement detection for smart interval adjustment
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './auth';

// Default intervals (in milliseconds)
const MOVING_INTERVAL = 30000;       // 30 seconds when moving
const STATIONARY_INTERVAL = 120000;  // 2 minutes when stationary
const LOW_BATTERY_INTERVAL = 180000; // 3 minutes on low battery (<20%)
const VERY_LOW_BATTERY_INTERVAL = 300000; // 5 minutes on very low battery (<10%)

// Movement detection thresholds
const MOVEMENT_THRESHOLD = 5; // meters - consider moved if > 5m from last position
const STATIONARY_SPEED = 0.5; // m/s - below this is considered stationary

export function useLocationTracking() {
  const { api, user } = useAuth();

  // Keep the latest API client without rebuilding tracking callbacks
  // whenever the authentication provider creates a new API object.
  const apiRef = useRef(api);

  useEffect(() => {
    apiRef.current = api;
  }, [api]);
  
  // State
  const [isTracking, setIsTracking] = useState(false);
  const [trackingStatus, setTrackingStatus] = useState(null);
  const [lastPing, setLastPing] = useState(null);
  
  const [livePosition, setLivePosition] = useState(null);
const [currentPosition, setCurrentPosition] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [pingCount, setPingCount] = useState(0);
  const [currentInterval, setCurrentInterval] = useState(MOVING_INTERVAL);
  
  // Refs
  const intervalRef = useRef(null);
  const watchIdRef = useRef(null);
  const lastPositionRef = useRef(null);
  const shiftIdRef = useRef(null);
  const isStartingRef = useRef(false);
  const lastPingSentAtRef = useRef(0);
  
  /**
   * Check if officer has an active shift that requires tracking
   */
  const checkTrackingStatus = useCallback(async () => {
    try {
      const response = await apiRef.current.get('/tracking/status');
      setTrackingStatus(response.data);
      
      // SUPPORT ACTIVE PATROLS / ROUTE RUNS EVEN WITHOUT SHIFT_ID
      const hasShift =
        !!response.data.shift_id;

      const hasPatrol =
        !!response.data.route_run_id ||
        !!response.data.patrol_run_id ||
        !!response.data.active_patrol ||
        !!response.data.active_route_run;

      const shouldTrack =
        response.data.tracking_enabled &&
        (hasShift || hasPatrol);

      if (shouldTrack) {
        // Prefer shift_id if available, otherwise fallback to patrol/run ids
        shiftIdRef.current =
          response.data.shift_id ||
          response.data.route_run_id ||
          response.data.patrol_run_id;

        console.log('[Tracking] Active tracking target detected', {
          shift_id: response.data.shift_id,
          route_run_id: response.data.route_run_id,
          patrol_run_id: response.data.patrol_run_id,
        });

        return true;
      }

      return false;
    } catch (error) {
      console.error('[Tracking] Status check failed:', error);
      setLastError('Failed to check tracking status');
      return false;
    }
  }, []);
  
  /**
   * Send GPS ping to backend
   */
  const sendPing = useCallback(async (position) => {
    const nowMs = Date.now();

    // P1 FIX: prevent ping storms from watchPosition + interval + remounts.
    // Allow max 1 backend ping every 25 seconds.
    if (nowMs - lastPingSentAtRef.current < 25000) {
      return;
    }
    lastPingSentAtRef.current = nowMs;

      const browserGpsPosition = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: Math.round(Number(position.coords.accuracy || 999)),
        timestamp: new Date().toISOString(),
      };
      setLivePosition(browserGpsPosition);
if (!shiftIdRef.current) {
      console.warn('[Tracking] No active shift, skipping ping');
      return;
    }
    
    const { latitude, longitude, accuracy, altitude, speed, heading } = position.coords;

    // Reject invalid / unstable GPS fixes
    if (
      !latitude ||
      !longitude ||
      accuracy <= 0 ||
      accuracy > 1000
    ) {
      console.log('[GPS] Ignoring unstable GPS fix', {
        latitude,
        longitude,
        accuracy
      });
      return;
    }
    
    // Get battery level if available
    let batteryLevel = null;
    let isCharging = null;
    try {
      if (navigator.getBattery) {
        const battery = await navigator.getBattery();
        batteryLevel = Math.round(battery.level * 100);
        isCharging = battery.charging;
      }
    } catch (e) {
      // Battery API not available
    }
    
    try {
      // P1 FIX: Send real GPS ping to backend so Command Centre can show officer live position.
      const response = await apiRef.current.post('/tracking/ping', {
        shift_id: shiftIdRef.current,
        latitude,
        longitude,
        accuracy,
        altitude,
        speed,
        heading,
        battery_level: batteryLevel,
        is_charging: isCharging,
        device_timestamp: new Date().toISOString(),
      });
      
      setLastPing({
        timestamp: new Date().toISOString(),
        latitude,
        longitude,
        response: response.data,
      });
      setPingCount(prev => prev + 1);
      setLastError(null);
      
      // Update interval based on backend recommendation and battery
      let nextInterval = Number(response.data?.next_ping_seconds || 30) * 1000;
      
      // Adjust for battery
      if (batteryLevel !== null) {
        if (batteryLevel < 10) {
          nextInterval = Math.max(nextInterval, VERY_LOW_BATTERY_INTERVAL);
        } else if (batteryLevel < 20) {
          nextInterval = Math.max(nextInterval, LOW_BATTERY_INTERVAL);
        }
      }
      
      setCurrentInterval(nextInterval);
      lastPositionRef.current = { latitude, longitude };
      
      console.log(`[Tracking] Ping sent: (${latitude.toFixed(5)}, ${longitude.toFixed(5)}) - next in ${nextInterval/1000}s`);
      
    } catch (error) {
      console.error('[Tracking] Ping failed:', error);
      setLastError('GPS ping failed: ' + (error.response?.data?.detail || error.message));
    }
  }, []);
  
  /**
   * Handle geolocation position update
   */
  const handlePosition = useCallback((position) => {
    // ALWAYS store live browser GPS immediately
    setCurrentPosition({
      coords: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: Math.round(Number(position.coords.accuracy || 999)),
        altitude: position.coords.altitude,
        speed: position.coords.speed,
        heading: position.coords.heading,
      },
      timestamp: position.timestamp,
    });

    // Backend ping is secondary
    sendPing(position);
  }, [sendPing]);
  
  /**
   * Handle geolocation error
   */
  const handleError = useCallback((error) => {
    console.error('[Tracking] Geolocation error:', error);
    let message = 'Location error';
    switch (error.code) {
      case error.PERMISSION_DENIED:
        message = 'Location permission denied';
        break;
      case error.POSITION_UNAVAILABLE:
        message = 'Location unavailable';
        break;
      case error.TIMEOUT:
        // STATIC SHIFT UX:
        // Do not aggressively show timeout errors during active static shifts.
        message = null;
        break;
      default:
        message = error.message;
    }
    setLastError(message);
  }, []);
  
  /**
   * Start location tracking
   */
  const startTracking = useCallback(async () => {
    // P1 FIX: prevent duplicate watchers/intervals causing endless refresh/lag.
    if (isTracking || isStartingRef.current || watchIdRef.current !== null || intervalRef.current !== null) {
      console.log('[Tracking] startTracking skipped - already active/starting');
      return true;
    }

    isStartingRef.current = true;

    // Start browser GPS first. Backend tracking status is secondary.
    // This prevents PatrolRunPage showing stale map / ±0m while waiting for tracking/status.
    const shouldTrack = await checkTrackingStatus();
    if (!shouldTrack) {
      console.warn('[Tracking] No backend tracking target yet, but browser GPS will still start');
    }
    
    // Check geolocation support
    if (!navigator.geolocation) {
      isStartingRef.current = false;
      setLastError('Geolocation not supported');
      return false;
    }
    
    // Request permission and get initial position
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        });
      });
      
      // Store first browser GPS immediately, then ping backend if possible
      handlePosition(position);
      
      // Start watching position
      watchIdRef.current = navigator.geolocation.watchPosition(
        handlePosition,
        handleError,
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
      
      // Set up interval for periodic updates (fallback if watch doesn't fire)
      intervalRef.current = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          handlePosition,
          handleError,
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0,
          }
        );
      }, MOVING_INTERVAL);
      
      setIsTracking(true);
      isStartingRef.current = false;
      console.log('[Tracking] Started tracking');
      return true;
      
    } catch (error) {
      isStartingRef.current = false;
      handleError(error);
      return false;
    }
  }, [checkTrackingStatus, handlePosition, handleError]);
  
  /**
   * Stop location tracking
   */
  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    setIsTracking(false);
    isStartingRef.current = false;
    shiftIdRef.current = null;
    console.log('[Tracking] Stopped tracking');
  }, []);
  
  /**
   * Force send a ping now
   */
  const forcePing = useCallback(async () => {
    if (!shiftIdRef.current) {
      await checkTrackingStatus();
    }
    
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          await sendPing(position);
          resolve(true);
        },
        (error) => {
          handleError(error);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
    });
  }, [checkTrackingStatus, sendPing, handleError]);
  
  // Update interval when it changes
  useEffect(() => {
    if (isTracking && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          handlePosition,
          handleError,
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0,
          }
        );
      }, currentInterval);
    }
  }, [currentInterval, isTracking, handlePosition, handleError]);
  
  // Auto-start once when an officer tracking hook is mounted.
  // Do not depend on isTracking: that previously caused an endless
  // start -> state update -> cleanup -> stop -> restart loop.
  useEffect(() => {
    const officerRole =
      user?.role === 'security_officer' ||
      user?.role === 'officer';

    if (!officerRole) {
      return undefined;
    }

    let cancelled = false;

    const initialiseTracking = async () => {
      if (cancelled) {
        return;
      }

      console.log('[Tracking] Auto-starting browser GPS tracking');
      await startTracking();
    };

    initialiseTracking();

    return () => {
      cancelled = true;
      stopTracking();
    };
  }, [user?.role, startTracking, stopTracking]);
  
  return {
    // State
    isTracking,
    trackingStatus,
    currentPosition,
    livePosition,
    lastPing,
    lastError,
    pingCount,
    currentInterval,
    
    // Actions
    startTracking,
    stopTracking,
    forcePing,
    checkTrackingStatus,
  };
}

export default useLocationTracking;
